import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Clock, Pencil, Truck } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';

import { BarraFiltros, type ChipFiltro } from '@/componentes/BarraFiltros';
import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao } from '@/componentes/Campo';
import { EtiquetaDoPreenchido, LinhaDoPreenchido } from '@/componentes/LinhaDoPreenchido';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { CardIndicador } from '@/componentes/CardIndicador';
import { ChaveDeFiltro } from '@/componentes/ChaveDeFiltro';
import { ControleCarregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { SemPermissao } from '@/componentes/SemPermissao';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { Medida } from '@/componentes/Medida';
import { RodapeDeContagem, RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { diaDaOperacao, janelaAteHoje } from '@/lib/dia-da-operacao';
import { formatarData, formatarDataHora, formatarDecimal, formatarInteiro, interpretarNumero } from '@/lib/formato';
import { opcaoDaMaquina } from '@/modulos/equipamentos/opcao-da-maquina';
import { AgendaDeParadas } from '@/modulos/oficina/AgendaDeParadas';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { validarBuscaDaOperacao, type BuscaDaOperacao } from './busca';
import { GavetaDeCorrecao, JanelaDeEstorno } from './CorrecaoDoTurno';
import { nomesDosTurnosDoDia, PERMISSAO_DE_CORRIGIR_TURNO } from './correcao';
import { andamentoDoTurno, medidorDo, problemaComALeituraSeguinte, problemaDasLeituras, type TipoDoMedidor } from './medidor';
import {
  explicarFalha,
  useApontamentos,
  useApontar,
  useCadastrosDaOperacao,
  useConferir,
  useHoras,
  useJanela,
  useLeiturasDoTurno,
  useParadas,
  useTurnos,
  useTurnosEstornados,
  useUltimoTurnoDaMaquina,
} from './consultas';
import { SaidasParaAObra } from './SaidasParaAObra';
import { linhasDosTurnos, rotuloDaChaveDeEstornados, textoDaCorrecao, textoDoEstorno, type LinhaDeTurno } from './estornados';
import type { ApontamentoDaApi, LeiturasDoTurno, ParadaDaApi } from './api';
import {
  horarioDoTurnoCadastrado,
  inicialSugerido,
  operadorSugerido,
  preencherSugestoes,
  textoDoCampo,
  turnoSugerido,
  type CampoSugerido,
} from './apontamento-sugerido';
import {
  HORAS_MAXIMAS_DO_TURNO,
  disponibilidadeDaFrota,
  evitaveisEmAberto,
  horasEvitaveis,
  paradasEmAberto,
  proporcaoDeEspera,
  somaDasHorasParadas,
  somaDoTurno,
  totaisDoPeriodo,
} from './horas';
import { usePaginacao } from './paginacao';
import { apagarRascunho, guardarRascunho, lerRascunho } from './rascunho';
import { contar, fraseDoErro } from './texto';
import { opcaoDaPessoa, useOpcoesComVisualDaFrota } from '@/modulos/equipamentos/opcoesComVisual';

/**
 * Operação — o turno apontado, as horas por natureza e o que tira
 * disponibilidade. Módulo 03 do mapa funcional v6.
 *
 * ## A frase que esta tela existe para não deixar ninguém somar errado
 *
 * Hora de motor **não soma** com produção, espera, deslocamento e manutenção.
 * O motor anda *durante* as outras — uma escavadeira esperando caminhão está
 * com o motor ligado e em espera ao mesmo tempo. As quatro particionam o
 * turno; o motor mede a máquina.
 *
 * Isso não é sutileza de modelagem: é a diferença entre um relatório certo e um
 * relatório que dá mais horas do que o dia tem. A tela separa as duas contas em
 * cards diferentes e escreve a distinção por extenso, porque quem olha dois
 * números lado a lado soma os dois — e aqui somá-los está errado.
 *
 * ## O que ela ainda não sabe, e diz
 *
 * **Disponibilidade** não aparece aqui, e a ausência é honesta: disponibilidade
 * é hora apontada dividida por hora DEVIDA, e a hora devida vem da janela
 * programada — que vem do turno cadastrado, que o dono ainda não definiu. O
 * sistema sabe quanto foi apontado e não sabe quanto era esperado. Mostrar uma
 * porcentagem inventada seria pior que não mostrar nenhuma.
 */

/** Um mês para trás, que é o período que o fechamento mensal olha. */
function periodoPadrao(): { de: string; ate: string } {
  return janelaAteHoje(30);
}

export function PaginaOperacao() {
  const padrao = useMemo(periodoPadrao, []);
  /* O FILTRO MORA NA URL (p.16). A rota ainda não declara `validateSearch`
     (isso é de `src/rotas`); a tela lê a busca crua e passa pelo mesmo tipo de
     validação que a conciliação usa, então um link torto cai no padrão. */
  const busca = validarBuscaDaOperacao(useSearch({ strict: false }) as Record<string, unknown>);
  const navegar = useNavigate();
  const de = busca.de ?? padrao.de;
  const ate = busca.ate ?? padrao.ate;
  const equipamentoFiltro = busca.equipamento ?? '';
  const apontando = busca.apontar === true;

  function irPara(proxima: BuscaDaOperacao) {
    void navegar({ to: '/operacao', search: proxima });
  }

  /** Muda um filtro e mantém os outros. Texto vazio tira o filtro da URL. */
  function filtrar(mudanca: { readonly de?: string; readonly ate?: string; readonly equipamento?: string }) {
    const proximo = { de: busca.de ?? '', ate: busca.ate ?? '', equipamento: equipamentoFiltro, ...mudanca };
    irPara({
      ...(proximo.de ? { de: proximo.de } : {}),
      ...(proximo.ate ? { ate: proximo.ate } : {}),
      ...(proximo.equipamento ? { equipamento: proximo.equipamento } : {}),
    });
  }

  const filtro = useMemo(
    () => ({
      de,
      ate,
      ...(equipamentoFiltro === '' ? {} : { equipamentoId: equipamentoFiltro }),
    }),
    [de, ate, equipamentoFiltro],
  );

  const cadastros = useCadastrosDaOperacao();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const apontamentos = useApontamentos(filtro);
  const horas = useHoras(filtro);
  const paradas = useParadas(
    equipamentoFiltro === '' ? {} : { equipamentoId: equipamentoFiltro },
  );
  const conferir = useConferir();
  /* ESTORNAR E CORRIGIR (ADR-008, 0086), pelos três pontos da linha. `null`
     na sessão quer dizer "a sessão não diz": o item aparece, e a rota confere
     (a mesma regra do livro do tanque). */
  const permissoes = useSessaoEmMemoria()?.permissoes ?? null;
  const pode = (codigo: string) => permissoes === null || permissoes.includes(codigo);
  const podeCorrigir = pode(PERMISSAO_DE_CORRIGIR_TURNO);
  /* Conferir e do encarregado (apontamento.conferir): sem a licenca, a linha
     nao oferece o botao que a rota recusaria (prova do marco, 07/10/2026). */
  const podeConferir = pode('apontamento.conferir');
  /* Apontar turno é de quem fecha o turno (Operador, Oficina): quem só lê a
     operação não recebe o botão nem o formulário (prova do marco, 07/10/2026). */
  const podeApontar = pode('apontamento.registrar');
  const [estornando, setEstornando] = useState<ApontamentoDaApi | null>(null);
  const [corrigindo, setCorrigindo] = useState<ApontamentoDaApi | null>(null);

  const base = useId();

  /* Os totais vêm de `horas.ts`, e não de uma conta escrita aqui.
     A separação não é estética: é lá que um teste alcança a aritmética, porque
     este projeto não renderiza tela em teste. E foi uma conta duplicada — a
     mesma fórmula na rota e na view do servidor — que deixou um defeito passar
     por doze asserções. Uma fórmula, um lugar, um teste. */
  const totais = useMemo(() => totaisDoPeriodo(horas.data ?? []), [horas.data]);
  const janela = useJanela(filtro);
  const disponivel = useMemo(() => disponibilidadeDaFrota(horas.data ?? []), [horas.data]);
  const espera = proporcaoDeEspera(totais);
  const evitaveis = useMemo(() => horasEvitaveis(paradas.data ?? []), [paradas.data]);
  const evitaveisAbertas = useMemo(() => evitaveisEmAberto(paradas.data ?? []), [paradas.data]);

  const recusaDeConferencia = conferir.isError ? explicarFalha(conferir.error) : null;

  const todos = useMemo(() => apontamentos.data ?? [], [apontamentos.data]);
  /* R06 (p.69): o estornado fica oculto atrás da chave; ligada, ele volta à
     posição original. A soma do período continua a de `todos`, os vigentes. */
  const estornados = useTurnosEstornados(filtro);
  const [mostrarEstornados, setMostrarEstornados] = useState(false);
  const quantosEstornados = estornados.data?.length ?? 0;
  const linhas = useMemo(
    () => linhasDosTurnos(todos, estornados.data ?? [], mostrarEstornados),
    [todos, estornados.data, mostrarEstornados],
  );
  const { visiveis, rodape } = usePaginacao(linhas);

  /* Filtro aplicado muda de aparência e ganha "✕" (p.16). O período sempre
     vale — o que conta como filtro é ele ter saído dos últimos 30 dias. */
  const periodoAlterado = de !== padrao.de || ate !== padrao.ate;
  const equipamentoEscolhido = (cadastros.data?.equipamentos ?? []).find((e) => e.id === equipamentoFiltro);
  const chips: readonly ChipFiltro[] = [
    ...(periodoAlterado
      ? [
          {
            id: 'periodo',
            rotulo: `Período: ${formatarData(new Date(`${de}T12:00:00`))} a ${formatarData(new Date(`${ate}T12:00:00`))}`,
            aoRemover: () => {
              filtrar({ de: '', ate: '' });
            },
          },
        ]
      : []),
    ...(equipamentoFiltro
      ? [
          {
            id: 'equipamento',
            rotulo: `Equipamento: ${equipamentoEscolhido?.codigo ?? 'escolhido'}`,
            aoRemover: () => {
              filtrar({ equipamento: '' });
            },
          },
        ]
      : []),
  ];

  function limparFiltros() {
    irPara({});
  }

  /* O estornado (R06, p.69): riscado, em cinza, com o motivo — nunca o
     mesmo peso do que vale. */
  const RISCADO = 'text-tinta-apagada line-through';
  const COLUNAS: readonly Coluna<LinhaDeTurno>[] = [
    {
      chave: 'dia',
      rotulo: 'Dia / turno',
      celula: (l) => (
        <span className="flex flex-col items-start">
          <span className={`txt-corpo ${l.tipo === 'estornado' ? 'text-tinta-apagada' : 'text-tinta'}`}>
            {`${formatarData(new Date(`${l.turno.dia}T12:00:00`))} · ${l.turno.turno}`}
          </span>
          {/* CORRIGIDO (R06, p.69): a etiqueta âmbar sob Dia/turno; o motivo
              abre ao passar o mouse (e vai ao leitor de tela no nome dela). */}
          {l.tipo === 'vigente' && l.turno.corrigeId ? (
            <span
              title={`Motivo: ${l.turno.motivoDaCorrecao ?? 'não registrado'}`}
              className="mt-1 inline-flex h-6 items-center gap-1 rounded-etiqueta bg-atencao-fundo px-2 txt-legenda text-atencao-texto"
            >
              <Pencil aria-hidden="true" className="size-3 shrink-0" />
              Corrigido
              <span className="sr-only">{`. Motivo: ${l.turno.motivoDaCorrecao ?? 'não registrado'}`}</span>
            </span>
          ) : null}
        </span>
      ),
    },
    {
      chave: 'equipamento',
      rotulo: 'Equipamento',
      celula: (l) => {
        if (l.tipo === 'estornado') {
          return (
            <span className="flex flex-col">
              <span className={`codigo ${RISCADO}`}>{l.turno.equipamento}</span>
              <TextoDaCelula texto={textoDoEstorno(l.turno)} classe="txt-apoio text-tinta-apagada" />
            </span>
          );
        }
        const correcao = textoDaCorrecao(l.turno);
        return (
          <span className="flex flex-col">
            {/* Código em monoespaçada (p.18), e ele não quebra. A segunda
                linha é a descrição — ou, no corrigido, a mudança (p.69). */}
            <span className="codigo text-tinta">{l.turno.equipamento}</span>
            <TextoDaCelula texto={correcao ?? l.turno.equipamentoDescricao} classe="txt-apoio text-tinta-secundaria" />
          </span>
        );
      },
    },
    {
      chave: 'operador',
      rotulo: 'Operador',
      /* Nome e atividade são texto livre (p.18): o nome em cima, a atividade
         em 12 px apagada embaixo. */
      celula: (l) =>
        l.tipo === 'estornado' ? (
          <TextoDaCelula texto={l.turno.operador ?? 'Não informado'} classe="txt-corpo text-tinta-apagada" />
        ) : (
          <span className="flex flex-col">
            <TextoDaCelula texto={l.turno.operador ?? 'Não informado'} classe="txt-corpo text-tinta" />
            {l.turno.atividade ? <TextoDaCelula texto={l.turno.atividade} classe="txt-apoio text-tinta-secundaria" /> : null}
          </span>
        ),
    },
    {
      chave: 'motor',
      rotulo: 'Motor / km',
      alinhamento: 'numero',
      celula: (l) => {
        if (l.tipo === 'estornado') return <span className="text-tinta-apagada">—</span>;
        /* O CAMINHÃO (0113): o odômetro não mede hora de motor, e a célula
           mostra o quilômetro do turno, com a unidade escrita. */
        const andou = andamentoDoTurno(l.turno);
        return andou.valor === null ? <span className="text-tinta-apagada">—</span> : <Medida valor={formatarDecimal(andou.valor, 1)} unidade={andou.unidade} />;
      },
      /* A soma é do FILTRO INTEIRO e só dos que VALEM: `todos`, e não as
         linhas que a tabela recebeu — nem a página, nem os estornados. Só
         horas de motor: km não soma com hora, e a nula do odômetro conta zero. */
      total: () => <Medida valor={formatarDecimal(todos.reduce((t, a) => t + (a.horasMotor === null ? 0 : Number(a.horasMotor)), 0), 1)} unidade="h" />,
    },
    {
      chave: 'apontadas',
      rotulo: 'Horas',
      alinhamento: 'numero',
      celula: (l) =>
        l.tipo === 'estornado' ? (
          <span className={`tabular-nums ${RISCADO}`}>
            {l.turno.horasApontadas === null ? '—' : `${formatarDecimal(Number(l.turno.horasApontadas), 1)} h`}
          </span>
        ) : (
          /* Blocos, e não flex: o alinhamento vem de quem desenha a célula. */
          <span className="block">
            <Medida valor={formatarDecimal(Number(l.turno.horasApontadas), 1)} unidade="h" />
            {/* Abaixo de 1280 a coluna Espera sai (R01, p.28), e a espera da
                linha passa a morar aqui, na segunda linha da célula. */}
            <span className="txt-linha2 block text-tinta-secundaria min-[1280px]:hidden">
              {`${formatarDecimal(Number(l.turno.horasEspera), 1)} h em espera${l.turno.causaCodigo ? ` · ${l.turno.causaCodigo}` : ''}`}
            </span>
          </span>
        ),
      total: () => <Medida valor={formatarDecimal(todos.reduce((t, a) => t + Number(a.horasApontadas), 0), 1)} unidade="h" />,
    },
    {
      chave: 'espera',
      rotulo: 'Espera',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (l) =>
        l.tipo === 'estornado' ? (
          <span className="text-tinta-apagada">—</span>
        ) : Number(l.turno.horasEspera) > 0 ? (
          <Medida valor={formatarDecimal(Number(l.turno.horasEspera), 1)} unidade="h" />
        ) : (
          <span className="text-tinta-apagada">—</span>
        ),
    },
    {
      /* A CAUSA DA ESPERA GANHA COLUNA PRÓPRIA (R06, p.69), com o código e o nome. */
      chave: 'causa',
      rotulo: 'Causa',
      prioridade: 'secundaria',
      celula: (l) =>
        l.turno.causaCodigo ? (
          <span className={`flex items-center gap-2 ${l.tipo === 'estornado' ? 'text-tinta-apagada' : 'text-tinta'}`}>
            <span className="mono shrink-0 rounded-etiqueta bg-rebaixada px-1 txt-legenda font-semibold">{l.turno.causaCodigo}</span>
            <TextoDaCelula texto={l.turno.causa ?? ''} classe="txt-apoio" />
          </span>
        ) : (
          <span className="text-tinta-apagada">—</span>
        ),
    },
    {
      chave: 'conferido',
      rotulo: 'Conferência',
      historia: 'COL-OPR-07',
      alinhamento: 'status',
      celula: (l) => {
        if (l.tipo === 'estornado') {
          /* O estornado não se confere: o selo diz o que ele é, escrito. */
          return (
            <span
              title={`${l.turno.estornadoPor ?? 'Alguém'} estornou em ${formatarDataHora(new Date(l.turno.estornadoEm))}`}
              className="inline-flex h-[var(--selo-altura)] items-center gap-1.5 rounded-pill bg-nao-alocado-fundo px-2 text-nao-alocado-texto"
            >
              <span aria-hidden="true" className="size-[var(--selo-ponto)] rounded-pill bg-nao-alocado" />
              <span className="txt-selo">Estornado</span>
            </span>
          );
        }
        const a = l.turno;
        return a.conferidoEm === null ? (
          /* RÓTULO ESCRITO, e não só uma cor (regra 2 do manual). */
          <span className="txt-corpo text-tinta">A conferir</span>
        ) : (
          <span className="flex flex-col">
            <span className="txt-corpo text-tinta">Conferido</span>
            <span className="txt-apoio text-tinta-secundaria">{a.conferidoPorNome ?? 'sem nome'}</span>
          </span>
        );
      },
    },
  ];

  const COLUNAS_PARADA: readonly Coluna<ParadaDaApi>[] = [
    {
      chave: 'motivo',
      rotulo: 'Causa',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="txt-corpo text-tinta">{p.motivo}</span>
          <span className="mono txt-apoio text-tinta-secundaria">{p.motivoCodigo}</span>
        </span>
      ),
    },
    {
      chave: 'evitavel',
      rotulo: 'Evitável',
      historia: 'FLT-OPR-04',
      alinhamento: 'status',
      /* A palavra, e não um sinal. Duas empresas com as mesmas horas paradas
         estão em situações diferentes se numa delas a maioria for chuva. */
      celula: (p) => (
        <span className="txt-corpo text-tinta">{p.evitavel ? 'Evitável' : 'Não evitável'}</span>
      ),
    },
    {
      chave: 'ocorrencias',
      rotulo: 'Ocorrências',
      alinhamento: 'numero',
      celula: (p) => <span className="tabular-nums">{formatarInteiro(Number(p.ocorrencias))}</span>,
    },
    {
      chave: 'horas',
      rotulo: 'Horas paradas',
      alinhamento: 'numero',
      celula: (p) =>
        p.horasParadas === null ? (
          /* Nulo quer dizer "todas ainda abertas". Mostrar zero afirmaria que a
             causa não custou nada, que é o contrário do que está acontecendo. */
          <span className="txt-apoio text-tinta-secundaria">ainda aberta</span>
        ) : (
          <Medida valor={formatarDecimal(Number(p.horasParadas), 1)} unidade="h" />
        ),
      /* A mesma regra do card: só as encerradas somam, e com toda causa
         ainda aberta não há soma — "0,0 h" contradiria as linhas acima. As
         abertas que ficaram de fora saem no total de "Em aberto", ao lado. */
      total: (linhas) => {
        const soma = somaDasHorasParadas(linhas);
        return soma === null ? (
          <span className="txt-apoio text-tinta-secundaria">—</span>
        ) : (
          <Medida valor={formatarDecimal(soma, 1)} unidade="h" />
        );
      },
    },
    {
      chave: 'aberto',
      rotulo: 'Em aberto',
      alinhamento: 'numero',
      celula: (p) =>
        Number(p.emAberto) === 0 ? (
          <span className="txt-apoio text-tinta-secundaria">—</span>
        ) : (
          <span className="tabular-nums">{formatarInteiro(Number(p.emAberto))}</span>
        ),
      total: (linhas) => {
        const abertas = paradasEmAberto(linhas);
        return abertas === 0 ? (
          <span className="txt-apoio text-tinta-secundaria">—</span>
        ) : (
          <span className="tabular-nums">{formatarInteiro(abertas)}</span>
        );
      },
    },
  ];

  function abrirApontamento() {
    irPara({ ...busca, apontar: true });
  }

  /* APONTAR É PÁGINA INTEIRA (R01, p.29): onze campos em três seções passam
     do limite do modal e da gaveta. Antes o formulário abria dentro da lista,
     entre o filtro e a tabela, empurrando os turnos para baixo. Agora ocupa a
     área de conteúdo, na grade da p.28, com a barra de ações fixa — e a
     página guarda o filtro, para voltar à mesma visão ao terminar. */
  if (apontando) {
    const voltar = () => {
      filtrar({});
    };
    return (
      <div className="min-w-0 flex-1 overflow-auto">
        <div className="pagina">
          <CabecalhoPagina
            sobretitulo="Operação"
            titulo="Apontar turno"
            subtitulo="O fechamento do turno de uma máquina: horímetro, horas por natureza e atividade"
          />
          {podeApontar ? (
            <FormularioDeApontamento aoFechar={voltar} equipamentoInicial={equipamentoFiltro} />
          ) : (
            <SemPermissao modulo="o apontamento do turno" perfil="Operador ou Oficina" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Operação"
          titulo="Turnos, horas e paradas"
          subtitulo="O que cada máquina fez, e por que deixou de trabalhar"
          acoes={
            /* SECUNDÁRIO À ESQUERDA, PRIMÁRIO À DIREITA (p.15), e UMA ação em
               laranja por tela. */
            <>
              <Link to="/operacao/cadastros" className="botao botao-secundario">
                Ver causas e turno
              </Link>
              {podeApontar ? (
                <HistoriaNaMesa codigo="BTN-OPR-06" className="flex">
                  <Botao nivel="primario" onClick={abrirApontamento}>
                    Apontar turno
                  </Botao>
                </HistoriaNaMesa>
              ) : null}
            </>
          }
        />

        <FaixaIndicadores>
          {/* A DISPONIBILIDADE entrou com o turno da empresa (decisão 19): sem
              janela programada não há denominador, e o cartão diz isso em vez de
              inventar um. As horas apontadas passam a ser o contexto dela. */}
          {/* Carregando (p.32): o rótulo e o ícone ficam, e o número vira
              esqueleto — antes, "0,0 h" e "—" no lugar do que não tinha chegado. */}
          <CardIndicador
            historia="IND-OPR-01"
            rotulo="Disponibilidade"
            carregando={horas.isPending}
            valor={disponivel === null ? '—' : formatarDecimal(disponivel.disponibilidade * 100, 1)}
            {...(disponivel === null ? {} : { unidade: '%', unidadeColada: true })}
            contexto={
              /* Vazio enquanto carrega: o card desenha a barra no lugar (p.32). */
              horas.isPending
                ? ''
                : disponivel === null
                  ? janela.data?.cadastrada === false
                    ? 'sem turno cadastrado: cadastre o turno da empresa em Causas e turno'
                    : `${formatarDecimal(totais.apontadas, 1)} h apontadas no período`
                  : `${formatarDecimal(totais.apontadas, 1)} h apontadas de ${formatarDecimal(disponivel.programadas, 1)} h programadas; ${formatarDecimal(disponivel.semApontamento, 1)} h sem apontamento`
            }
            icone={Truck}
            tom="operando"
            {...(disponivel === null ? {} : { proporcao: disponivel.disponibilidade })}
          />
          <CardIndicador
            historia="IND-OPR-02"
            rotulo="Horas de motor"
            carregando={horas.isPending}
            valor={formatarDecimal(totais.motor, 1)}
            unidade="h"
            /* O CONTEXTO CARREGA O AVISO. Dois números lado a lado convidam à
               soma, e aqui somar está errado: o motor anda durante a produção,
               a espera e o deslocamento. */
            contexto="medida do horímetro; não soma com as apontadas, acontece durante elas"
            icone={Clock}
            /* Todo card tem ícone em bloco COLORIDO (R02, regra 13): o neutro
               era tinta cinza. O tom da marca é o do total da frota no exemplo
               da R02 — o motor é a medida da máquina, e não um status. */
            tom="marca"
          />
          <CardIndicador
            historia="IND-OPR-03"
            rotulo="Horas em espera"
            carregando={horas.isPending}
            valor={formatarDecimal(totais.espera, 1)}
            unidade="h"
            contexto={
              horas.isPending
                ? ''
                : espera === null
                  ? 'sem apontamento no período'
                  : `${formatarDecimal(espera, 1)}% do que foi apontado`
            }
            icone={Clock}
            tom="standby"
            {...(espera === null ? {} : { proporcao: espera / 100 })}
          />
          <CardIndicador
            rotulo="Paradas evitáveis"
            carregando={paradas.isPending}
            /* Parada evitável aberta NÃO vale zero: o card diz as horas das
               encerradas e conta as abertas no contexto — ou "—", se nenhuma
               encerrou. É a mesma regra da coluna "Horas paradas" abaixo. */
            valor={evitaveis === null ? '—' : formatarDecimal(evitaveis, 1)}
            {...(evitaveis === null ? {} : { unidade: 'h' })}
            contexto={
              paradas.isPending
                ? ''
                : evitaveis === null
                  ? contar(evitaveisAbertas, 'parada ainda aberta', 'paradas ainda abertas')
                  : evitaveisAbertas > 0
                    ? `${contar(evitaveisAbertas, 'parada ainda aberta', 'paradas ainda abertas')}, fora da soma`
                    : 'causas que a operação pode atacar; chuva e frente não liberada ficam de fora'
            }
            icone={Clock}
            tom="parada"
          />
        </FaixaIndicadores>

        {/* AS PARADAS DE HOJE (0089): a O.S. que a manutenção combinou com a
            frente. Fica acima do filtro porque não depende dele — é sempre o
            dia de hoje, e o período e a máquina filtram só os turnos abaixo. */}
        <AgendaDeParadas pode={pode} />

        {/* O APARELHO QUE NAO VOLTOU DA OBRA (0112). Fica aqui, acima do
            filtro e ao lado da agenda, pelo mesmo motivo dela: nao depende do
            recorte — e o que esta pendente agora — e desaparece quando nao ha
            nada a cobrar. */}
        <SaidasParaAObra pode={pode} />

        {/* ------------------------------------------------------- o filtro */}
        {/* Filtro aplicado muda de aparência e ganha "✕"; a lista filtrada
            mostra a contagem; "Limpar filtros" só quando há o que limpar (p.16). */}
        <BarraFiltros
          chips={chips}
          /* Enquanto os turnos vêm, a contagem vira barra (p.32), e quem diz
             que carrega é a tabela — a frase aqui seria uma segunda região. */
          contagem={apontamentos.isPending ? null : contar(todos.length, 'turno apontado', 'turnos apontados')}
          aoLimpar={limparFiltros}
        >
          <Campo id={`${base}-de`} rotulo="De">
            <input
              id={`${base}-de`}
              type="date"
              className={`controle ${periodoAlterado ? 'border-marca bg-marca-fundo' : ''}`}
              value={de}
              onChange={(e) => {
                filtrar({ de: e.target.value });
              }}
            />
          </Campo>
          <Campo id={`${base}-ate`} rotulo="Até">
            <input
              id={`${base}-ate`}
              type="date"
              className={`controle ${periodoAlterado ? 'border-marca bg-marca-fundo' : ''}`}
              value={ate}
              onChange={(e) => {
                filtrar({ ate: e.target.value });
              }}
            />
          </Campo>
          {cadastros.isPending ? (
            /* As máquinas ainda vêm (p.32): o rótulo fica e só o controle vira
               barra, na largura de um "código — descrição" — a lista só com
               "Todos" escondia o filtro vindo da URL. */
            <div className="w-64">
              <Campo id={`${base}-equipamento`} rotulo="Equipamento">
                <ControleCarregando oQue="Carregando os equipamentos" />
              </Campo>
            </div>
          ) : (
            <CampoSelecao
              rotulo="Equipamento"
              valor={equipamentoFiltro}
              rotuloVazio="Todos"
              aplicado={equipamentoFiltro !== ''}
              opcoes={comVisualDaFrota((cadastros.data?.equipamentos ?? []).map((e) => opcaoDaMaquina(e)))}
              aoMudar={(valor) => {
                filtrar({ equipamento: valor });
              }}
              adesivo
            />
          )}
        </BarraFiltros>

        {recusaDeConferencia === null ? null : (
          <div
            role="alert"
            className="flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4"
          >
            <span className="txt-corpo text-tinta">{fraseDoErro(recusaDeConferencia.erro)}</span>
            <span className="txt-apoio text-tinta-secundaria">
              {recusaDeConferencia.comoResolver}
            </span>
          </div>
        )}

        {/* --------------------------------------------------- os turnos */}
        {/* A chave dos estornados (R06, p.69), no cabeçalho da lista. Só
            aparece quando há estornado no recorte: uma chave que não muda
            nada não entra. */}
        {quantosEstornados > 0 ? (
          <div className="flex justify-end">
            <ChaveDeFiltro
              rotulo={rotuloDaChaveDeEstornados(quantosEstornados)}
              ligada={mostrarEstornados}
              aoMudar={setMostrarEstornados}
            />
          </div>
        ) : null}
        <Tabela
          rotulo="Apontamentos de turno"
          colunas={COLUNAS}
          linhas={visiveis}
          alturaDupla
          rotuloDoTotal="Soma do período"
          chaveDaLinha={(l) => l.id}
          /* AÇÃO DA LINHA na última célula, à direita e no hover (p.18): oito
             "Conferir turno" laranja sempre acesos competiam com a ação
             primária. Terciário — a ação primária é da página (p.15). */
          acoes={(l) =>
            podeConferir && l.tipo === 'vigente' && l.turno.conferidoEm === null ? (
              <Botao
                nivel="terciario"
                disabled={conferir.isPending}
                onClick={() => {
                  conferir.mutate({ id: l.id });
                }}
              >
                Conferir turno
              </Botao>
            ) : null
          }
          /* Os três pontos (p.18): estornar e corrigir são exceção, com
             licença própria, e ficam fora da ação da linha. */
          menu={(l) => {
            if (!podeCorrigir || l.tipo === 'estornado') return null;
            const a = l.turno;
            const mais: AcaoDoMenu[] = [
              {
                rotulo: 'Corrigir turno',
                aoEscolher: () => {
                  setCorrigindo(a);
                },
              },
              {
                rotulo: 'Estornar turno',
                aoEscolher: () => {
                  setEstornando(a);
                },
              },
            ];
            return <MaisAcoes forma="icone" rotulo={`Mais ações do turno de ${a.equipamento}`} acoes={mais} />;
          }}
          carregando={apontamentos.isPending}
          erro={
            apontamentos.isError ? (
              <EstadoVazio
                titulo="Não foi possível carregar os apontamentos"
                descricao={explicarFalha(apontamentos.error).comoResolver}
                acao={
                  <Botao
                    nivel="secundario"
                    onClick={() => {
                      void apontamentos.refetch();
                    }}
                  >
                    Carregar de novo
                  </Botao>
                }
              />
            ) : null
          }
          vazio={
            chips.length > 0 ? (
              /* Lista vazia POR FILTRO (p.23): a saída é limpar, e não apontar
                 um turno que talvez já exista fora do filtro. */
              <EstadoVazio
                titulo="Nenhum turno com esses filtros"
                descricao="Troque o período ou o equipamento, ou limpe os filtros para ver os últimos 30 dias da frota."
                acao={
                  <Botao nivel="secundario" onClick={limparFiltros}>
                    Limpar filtros
                  </Botao>
                }
              />
            ) : (
              <EstadoVazio
                titulo="Nenhum turno apontado neste período"
                descricao="O apontamento é o fechamento de um turno: horímetro, horas por natureza e a atividade. Sem ele o sistema não sabe o que a máquina fez."
                acao={
                  podeApontar ? (
                    <Botao nivel="secundario" onClick={abrirApontamento}>
                      Apontar turno
                    </Botao>
                  ) : undefined
                }
              />
            )
          }
          rodape={<RodapeTabela {...rodape} />}
        />
        <JanelaDeEstorno
          turno={estornando}
          aoFechar={() => {
            setEstornando(null);
          }}
        />
        <GavetaDeCorrecao
          turno={corrigindo}
          aoFechar={() => {
            setCorrigindo(null);
          }}
          aoEstornar={(t) => {
            setCorrigindo(null);
            setEstornando(t);
          }}
        />

        {/* ------------------------------------- o que tira disponibilidade */}
        <div className="flex flex-col gap-2">
          <h2 className="txt-secao text-tinta">O que está tirando disponibilidade</h2>
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Agrupado por causa, e não por parada. Uma lista de paradas diz o que aconteceu; a soma
            por causa diz o que atacar.
          </p>
        </div>

        <HistoriaNaMesa codigo="GRF-OPR-05">
          <Tabela
            rotulo="Paradas por causa"
            colunas={COLUNAS_PARADA}
            linhas={paradas.data ?? []}
            /* Uma linha por causa do catálogo, e a Soma vale para TODAS: paginar
               faria a soma ser só da página. Contagem sem paginação (p.26). */
            rodape={<RodapeDeContagem total={(paradas.data ?? []).length} />}
            alturaDupla
            rotuloDoTotal="Soma"
            chaveDaLinha={(p) => p.motivoId}
            carregando={paradas.isPending}
            erro={
              paradas.isError ? (
                <EstadoVazio
                  titulo="Não foi possível carregar as paradas"
                  descricao={explicarFalha(paradas.error).comoResolver}
                  acao={
                    <Botao
                      nivel="secundario"
                      onClick={() => {
                        void paradas.refetch();
                      }}
                    >
                      Carregar de novo
                    </Botao>
                  }
                />
              ) : null
            }
            vazio={
              <EstadoVazio
                titulo="Nenhuma parada com causa registrada"
                descricao="Paradas e manutenções passaram a exigir uma causa escolhida na lista. Cadastre as causas primeiro; depois registre a situação da máquina pela ficha dela."
                acao={
                  <Link to="/operacao/cadastros" className="botao botao-secundario">
                    Ver as causas de parada
                  </Link>
                }
              />
            }
          />
        </HistoriaNaMesa>
      </div>
    </div>
  );
}

/* ========================================================================
 * O FORMULÁRIO
 * ===================================================================== */

/** O que a pessoa digitou, tudo texto, como o campo guarda. É isto que vira rascunho. */
interface ValoresDoApontamento {
  readonly equipamentoId: string;
  readonly dia: string;
  readonly turno: string;
  readonly operadorId: string;
  readonly atividade: string;
  readonly inicial: string;
  readonly final: string;
  readonly producao: string;
  readonly espera: string;
  readonly deslocamento: string;
  readonly manutencao: string;
  /** A causa da hora parada (0086): o id do catálogo, ou vazio. */
  readonly causaId: string;
  /** A hora do turno (0121), hh:mm ou vazio: o fim é quando o horímetro final foi lido. */
  readonly inicio: string;
  readonly fim: string;
}

function valoresIniciais(equipamentoId = ''): ValoresDoApontamento {
  return {
    equipamentoId,
    /* Hoje em São Paulo, e não em UTC: depois das 21:00 o UTC já é amanhã. */
    dia: diaDaOperacao(),
    /* Vazio, e não "1º turno": quem escreve o turno é a sugestão (o turno
       cadastrado do dia, o do último turno da máquina ou "1º turno"). */
    turno: '',
    operadorId: '',
    atividade: '',
    inicial: '',
    final: '',
    producao: '',
    espera: '',
    deslocamento: '',
    manutencao: '',
    causaId: '',
    inicio: '',
    fim: '',
  };
}

const CHAVES_DO_APONTAMENTO = Object.keys(valoresIniciais()) as readonly (keyof ValoresDoApontamento)[];
const CAMPOS_SUGERIDOS: readonly CampoSugerido[] = ['turno', 'inicial', 'operadorId'];

/** O rascunho lido de volta só vale com a forma certa, campo a campo. */
function valoresDoRascunho(valor: unknown): ValoresDoApontamento | null {
  if (typeof valor !== 'object' || valor === null) return null;
  /* O rascunho de antes da causa (0086) não a tem: vale "sem causa". */
  const lido: Record<string, unknown> = { causaId: '', inicio: '', fim: '', ...(valor as Record<string, unknown>) };
  if (!CHAVES_DO_APONTAMENTO.every((chave) => typeof lido[chave] === 'string')) return null;
  return lido as unknown as ValoresDoApontamento;
}

/**
 * Nada digitado: o dia de hoje, a máquina que veio do filtro e o que o sistema
 * propôs (os campos marcados) são o padrão, não digitação.
 */
function nadaDigitado(valores: ValoresDoApontamento, padrao: ValoresDoApontamento, marcados: ReadonlySet<CampoSugerido>): boolean {
  return CHAVES_DO_APONTAMENTO.every(
    (chave) => valores[chave] === padrao[chave] || marcados.has(chave as CampoSugerido),
  );
}

/** Os campos que têm regra, na ordem da tela: é a ordem do foco quando o envio recusa. */
const CAMPOS_COM_REGRA = ['equipamento', 'turno', 'inicial', 'final', 'soma', 'causa', 'hora'] as const;
type CampoComRegra = (typeof CAMPOS_COM_REGRA)[number];
type Erros = Readonly<Record<CampoComRegra, string | null>>;
const SEM_ERRO: Erros = { equipamento: null, turno: null, hora: null, inicial: null, final: null, soma: null, causa: null };

/**
 * A HORA DO TURNO (0121): os dois juntos, ou nenhum. O fim é o instante em que
 * o horímetro final foi lido — sem ele, a leitura ficava às 23:59 e recusava o
 * abastecimento da tarde do mesmo dia (robôs no QAS, 20/10/2026).
 */
function mensagemDaHora(inicio: string, fim: string): string | null {
  if ((inicio === '') !== (fim === '')) return 'Informe o início e o fim do turno, ou deixe os dois em branco.';
  if (inicio !== '' && inicio === fim) return 'O turno começa e termina na mesma hora. Confira o fim.';
  return null;
}

/** A causa explica hora parada (0086): pede espera ou manutenção no turno. */
function mensagemDaCausa(causaId: string, espera: string, manutencao: string): string | null {
  if (causaId === '') return null;
  return horasParadas(espera, manutencao) <= 0
    ? 'A causa explica hora parada: informe a espera ou a manutenção do turno, ou deixe sem causa.'
    : null;
}

function horasDoCampo(texto: string): number {
  return texto.trim() === '' ? 0 : (interpretarNumero(texto) ?? 0);
}

/** A espera e a manutenção do turno: é com elas que a causa da parada aparece à vista. */
function horasParadas(espera: string, manutencao: string): number {
  return horasDoCampo(espera) + horasDoCampo(manutencao);
}

/**
 * As mesmas regras de antes, agora UMA POR CAMPO — é o que permite validar ao
 * sair do campo (p.16; R01, p.28) em vez de só no envio. O final também é
 * conferido contra a leitura registrada DEPOIS deste turno, a mesma conta do
 * celular: o banco recusaria (0119), e a tela diz antes.
 */
function mensagemDe(
  campo: CampoComRegra,
  v: ValoresDoApontamento,
  tipoMedidor: TipoDoMedidor,
  depois: LeiturasDoTurno['depois'] | undefined,
): string | null {
  const medidor = medidorDo(tipoMedidor);
  switch (campo) {
    case 'equipamento':
      return v.equipamentoId === '' ? 'Escolha a máquina do turno.' : null;
    case 'turno':
      return v.turno.trim() === '' ? 'Diga de que turno é este apontamento.' : null;
    case 'hora':
      return mensagemDaHora(v.inicio, v.fim);
    case 'inicial':
      return interpretarNumero(v.inicial) === null ? `Informe o ${medidor.noMeio} do início.` : null;
    case 'final': {
      const final = interpretarNumero(v.final);
      if (final === null) return `Informe o ${medidor.noMeio} do fim.`;
      const inicial = interpretarNumero(v.inicial);
      /* A mesma regra da correção e do celular, com o teto da unidade (0113). */
      return (inicial === null ? null : problemaDasLeituras(medidor.tipo, inicial, final)) ?? problemaComALeituraSeguinte(medidor.tipo, final, depois);
    }
    case 'soma':
      return somaDoTurno(v) > HORAS_MAXIMAS_DO_TURNO
        ? 'As quatro somam mais de 24 horas. Confira a distribuição do turno.'
        : null;
    case 'causa':
      return mensagemDaCausa(v.causaId, v.espera, v.manutencao);
  }
}

/**
 * Apontar turno — o fechamento do turno de uma máquina.
 *
 * ## Duas camadas, como o lançamento de abastecimento (31/10/2026)
 *
 * O dono: *"As melhorias que fizemos no formulário de combustível devem ser
 * levadas aos outros formulários do sistema também"*, e o sistema é usado por
 * pessoas leigas, sem rolar e mexer no mouse à toa.
 *
 *  - **À vista, o que se digita todo dia**, em três linhas da grade: máquina e
 *    dia; turno e os dois horímetros; as quatro horas do turno, lado a lado. A
 *    causa da parada aparece ali quando há espera ou manutenção, que é quando
 *    ela explica alguma coisa.
 *  - **O que o sistema já sabe** (o operador habitual, o horário do turno
 *    cadastrado, a atividade) vai em ETIQUETA, numa linha só, e os campos
 *    abrem em "Ajustar operador e horário".
 *
 * ## O que vem preenchido, e de onde (`apontamento-sugerido.ts`)
 *
 * O turno cadastrado do dia; o horímetro do começo, que é a leitura antes do
 * turno (o fim do último turno); o operador do último turno da máquina; e a
 * máquina do filtro da lista, quando a pessoa veio de uma. O campo preenchido
 * leva "· pré-preenchido" e a ajuda diz de onde veio; quem mexe, manda — a
 * sugestão seguinte não passa por cima.
 *
 * Nenhum campo saiu e nenhuma regra mudou: a validação continua ao sair do
 * campo, e o motor é calculado a cada tecla.
 */
function FormularioDeApontamento({ aoFechar, equipamentoInicial }: { readonly aoFechar: () => void; readonly equipamentoInicial: string }) {
  const base = useId();
  const cadastros = useCadastrosDaOperacao();
  const comVisualDaFrota = useOpcoesComVisualDaFrota();
  const turnos = useTurnos();
  const apontar = useApontar();
  const usuario = useSessaoEmMemoria();
  /* RASCUNHO AUTOMÁTICO (p.16): onze campos. A chave leva o usuário — no
     computador dividido do escritório da obra, um não abre o turno do outro. */
  const chaveDoRascunho = usuario === null ? null : `apontamento:${usuario.usuarioId}`;
  const [padrao] = useState(() => valoresIniciais(equipamentoInicial));

  const [recuperado, setRecuperado] = useState(() => {
    const guardado = chaveDoRascunho === null ? null : lerRascunho(chaveDoRascunho);
    const valores = guardado === null ? null : valoresDoRascunho(guardado.valor);
    return guardado === null || valores === null ? null : { valores, salvoEm: guardado.salvoEm };
  });
  const [v, setV] = useState<ValoresDoApontamento>(() => recuperado?.valores ?? padrao);
  /* O QUE A PESSOA TOCOU: a sugestão não passa por cima. O rascunho recuperado
     volta tocado no que tem valor — não há como saber o que era sugestão. */
  const [tocados, setTocados] = useState<ReadonlySet<CampoSugerido>>(
    () => new Set(recuperado === null ? [] : CAMPOS_SUGERIDOS.filter((c) => recuperado.valores[c] !== '')),
  );
  const [erros, setErros] = useState<Erros>(SEM_ERRO);
  const [recusa, setRecusa] = useState<{ erro: string; comoResolver: string } | null>(null);
  /* A GAVETA de operador, horário e atividade: fechada por padrão. */
  const [ajustando, setAjustando] = useState(false);

  const equipamentos = cadastros.data?.equipamentos ?? [];
  const maquina = equipamentos.find((e) => e.id === v.equipamentoId);
  /* O MEDIDOR DA MÁQUINA ESCOLHIDA (0113): o caminhão aponta em km, e o
     rótulo, a unidade e o teto mudam com ela. */
  const medidor = medidorDo(maquina?.tipoMedidor);

  /* ---- O que o sistema sabe ---- */
  const ultimoTurno = useUltimoTurnoDaMaquina(v.equipamentoId);
  const ultimo = ultimoTurno.data ?? null;
  const leituras = useLeiturasDoTurno(v.equipamentoId, v.dia, v.turno.trim(), v.inicio !== '' && v.fim !== '' ? `${v.inicio}-${v.fim}` : '');
  const leiturasEsperando = leituras.isPlaceholderData || (leituras.isFetching && !leituras.data);
  const comMaquina = v.equipamentoId !== '';
  const ultimoDecidido = !comMaquina || !ultimoTurno.isPending;

  const turnoProposto = turnos.isPending || !ultimoDecidido
    ? null
    : turnoSugerido({ turnos: turnos.data ?? [], dia: v.dia, hoje: diaDaOperacao(), agora: new Date(), ultimo });
  const inicialProposto = comMaquina && ultimoDecidido && !leiturasEsperando ? inicialSugerido(leituras.data, ultimo, false) : null;
  const operadorProposto =
    comMaquina && ultimoDecidido && !leiturasEsperando
      ? operadorSugerido({
          ultimo,
          eu: usuario?.usuarioId ?? null,
          euApto: leituras.data?.logadoApto === true,
          operadores: cadastros.data?.operadores ?? [],
        })
      : null;

  /* Só entra na conta o que já se decidiu: enquanto a consulta vem, o campo
     fica como está, em vez de piscar vazio e voltar. */
  const sugestaoDoTurno = turnoProposto?.nome;
  const sugestaoDoInicial = !comMaquina ? null : ultimoDecidido && !leiturasEsperando ? (inicialProposto?.texto ?? null) : undefined;
  const sugestaoDoOperador = !comMaquina ? null : ultimoDecidido && !leiturasEsperando ? (operadorProposto?.id ?? null) : undefined;
  useEffect(() => {
    const sugestoes: Partial<Record<CampoSugerido, string | null>> = {
      ...(sugestaoDoTurno === undefined ? {} : { turno: sugestaoDoTurno }),
      ...(sugestaoDoInicial === undefined ? {} : { inicial: sugestaoDoInicial }),
      ...(sugestaoDoOperador === undefined ? {} : { operadorId: sugestaoDoOperador }),
    };
    setV((atual) => preencherSugestoes(atual, tocados, sugestoes).valores);
  }, [sugestaoDoTurno, sugestaoDoInicial, sugestaoDoOperador, tocados]);

  /** O campo está com o valor que o sistema escreveu, e a pessoa não mexeu. */
  const marcado = (campo: CampoSugerido) => {
    const sugestao = campo === 'turno' ? sugestaoDoTurno : campo === 'inicial' ? sugestaoDoInicial : sugestaoDoOperador;
    return !tocados.has(campo) && typeof sugestao === 'string' && sugestao !== '' && v[campo] === sugestao;
  };
  const marcados = new Set(CAMPOS_SUGERIDOS.filter(marcado));
  const tudoPadrao = nadaDigitado(v, padrao, marcados);

  useEffect(() => {
    if (chaveDoRascunho === null) return;
    if (tudoPadrao) apagarRascunho(chaveDoRascunho);
    else guardarRascunho(chaveDoRascunho, v);
  }, [v, tudoPadrao, chaveDoRascunho]);

  /* A soma das QUATRO, mostrada enquanto se digita — de `horas.ts`, que é onde
     o teste a alcança. O motor não é parâmetro daquela função, e a ausência é o
     ponto: não há como incluí-lo por descuido. */
  const somaDasQuatro = somaDoTurno(v);
  const motor = v.inicial.trim() === '' || v.final.trim() === '' ? null : horasDoCampo(v.final) - horasDoCampo(v.inicial);
  const nenhumaHora = [v.producao, v.espera, v.deslocamento, v.manutencao].every((t) => t.trim() === '');
  /* A CAUSA À VISTA quando há hora parada: é quando ela explica alguma coisa.
     Sem espera nem manutenção, ela mora na gaveta, a um clique. */
  const causaAVista = horasParadas(v.espera, v.manutencao) > 0 || v.causaId !== '';

  const idDe = (campo: CampoComRegra) => `${base}-${campo === 'soma' ? 'producao' : campo}`;
  /* Os turnos da empresa naquele dia da semana (0027) vão como sugestão: o
     nome de um turno cadastrado diz a hora do fim, e é nela que o horímetro
     do turno é gravado (0086) — dois turnos no mesmo dia passam a ter ordem. */
  const turnosDoDia = nomesDosTurnosDoDia(turnos.data ?? [], v.dia);
  const horarioCadastrado = horarioDoTurnoCadastrado(turnos.data ?? [], v.dia, v.turno);
  const depois = leituras.isPlaceholderData ? undefined : leituras.data?.depois;

  /** Muda um valor e apaga SÓ o erro daquele campo: o dos outros continua na tela até ser corrigido. */
  function mudar(chave: keyof ValoresDoApontamento, valor: string, erroQueSome?: CampoComRegra) {
    setV((atual) => ({ ...atual, [chave]: valor }));
    if ((CAMPOS_SUGERIDOS as readonly string[]).includes(chave)) {
      setTocados((atual) => new Set([...atual, chave as CampoSugerido]));
    }
    if (erroQueSome) setErros((atual) => ({ ...atual, [erroQueSome]: null }));
  }

  /** A máquina nova traz o horímetro DELA: o início digitado para a outra volta a ser sugestão. */
  function escolherMaquina(id: string) {
    setV((atual) => ({ ...atual, equipamentoId: id }));
    setTocados((atual) => new Set([...atual].filter((c) => c !== 'inicial')));
    setErros((atual) => ({ ...atual, equipamento: null, inicial: null, final: null }));
  }

  /** Validação ao SAIR do campo, e não a cada tecla (p.16). */
  function conferir(...campos: readonly CampoComRegra[]) {
    setErros((atual) => {
      const proximo = { ...atual };
      for (const campo of campos) proximo[campo] = mensagemDe(campo, v, medidor.tipo, depois);
      return proximo;
    });
  }

  /** `aria-invalid` e a frase de erro ligadas ao campo, para o leitor de tela ler as duas juntas. */
  const invalido = (campo: CampoComRegra, id: string) =>
    erros[campo] ? { 'aria-invalid': true as const, 'aria-describedby': `${id}-erro` } : {};

  function descartarRascunho() {
    if (chaveDoRascunho !== null) apagarRascunho(chaveDoRascunho);
    setV(padrao);
    setTocados(new Set());
    setErros(SEM_ERRO);
    setRecuperado(null);
  }

  function cancelar() {
    if (chaveDoRascunho !== null) apagarRascunho(chaveDoRascunho);
    aoFechar();
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const achados = Object.fromEntries(CAMPOS_COM_REGRA.map((campo) => [campo, mensagemDe(campo, v, medidor.tipo, depois)])) as Erros;
    setErros(achados);

    /* No envio, revalida tudo e leva o foco ao primeiro campo recusado. O que
       mora na gaveta (a hora, a causa sem hora parada) abre a gaveta antes. */
    const primeiro = CAMPOS_COM_REGRA.find((campo) => achados[campo] !== null);
    if (primeiro) {
      if (primeiro === 'hora' || (primeiro === 'causa' && !causaAVista)) setAjustando(true);
      window.requestAnimationFrame(() => {
        document.getElementById(idDe(primeiro))?.focus();
      });
      return;
    }

    setRecusa(null);
    apontar.mutate(
      {
        equipamentoId: v.equipamentoId,
        dia: v.dia,
        turno: v.turno.trim(),
        ...(v.inicio !== '' && v.fim !== '' ? { inicio: v.inicio, fim: v.fim } : {}),
        operadorId: v.operadorId === '' ? null : v.operadorId,
        atividade: v.atividade.trim() === '' ? null : v.atividade.trim(),
        horimetroInicial: horasDoCampo(v.inicial),
        horimetroFinal: horasDoCampo(v.final),
        horasProducao: horasDoCampo(v.producao),
        horasEspera: horasDoCampo(v.espera),
        horasDeslocamento: horasDoCampo(v.deslocamento),
        horasManutencao: horasDoCampo(v.manutencao),
        causaId: v.causaId === '' ? null : v.causaId,
      },
      {
        onSuccess: () => {
          if (chaveDoRascunho !== null) apagarRascunho(chaveDoRascunho);
          aoFechar();
        },
        onError: (falha) => {
          setRecusa(explicarFalha(falha));
        },
      },
    );
  }

  const hora = (chave: 'producao' | 'espera' | 'deslocamento' | 'manutencao', rotulo: string) => (
    <Campo id={`${base}-${chave}`} rotulo={rotulo}>
      <div className="flex items-center gap-2">
        <input
          id={`${base}-${chave}`}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          className="controle w-full min-w-0"
          placeholder="0,0"
          value={v[chave]}
          /* A regra é da SOMA das quatro: o erro mora sob "Somam … do turno",
             e as quatro apontam para ele. */
          {...(erros.soma ? { 'aria-invalid': true, 'aria-describedby': `${base}-soma-erro` } : {})}
          onChange={(e) => {
            mudar(chave, e.target.value, 'soma');
            if (chave === 'espera' || chave === 'manutencao') setErros((atual) => ({ ...atual, causa: null }));
          }}
          onBlur={() => {
            conferir('soma');
          }}
        />
        <span className="txt-corpo shrink-0 text-tinta-apagada">h</span>
      </div>
    </Campo>
  );

  const codigo = maquina?.codigo ?? 'a máquina';
  const dataDoUltimo = ultimo ? formatarData(new Date(`${ultimo.dia}T12:00:00`)) : '';
  const ajudaDoTurno = marcado('turno')
    ? turnoProposto?.origem === 'cadastro'
      ? horarioCadastrado
        ? `Turno da empresa neste dia, das ${horarioCadastrado.inicio} às ${horarioCadastrado.fim}.`
        : 'Turno da empresa neste dia.'
      : turnoProposto?.origem === 'ultimo'
        ? `O mesmo do último turno de ${codigo}.`
        : undefined
    : undefined;
  const ajudaDoInicial = marcado('inicial')
    ? inicialProposto?.origem === 'ultimo-turno'
      ? `Fim do último turno de ${codigo}, em ${dataDoUltimo}.`
      : 'A última leitura antes deste turno.'
    : comMaquina && !ultimoDecidido
      ? undefined
      : comMaquina && inicialProposto === null && !tocados.has('inicial')
        ? `Nenhuma leitura de ${codigo} antes deste turno: confira no painel da máquina.`
        : undefined;
  const ajudaDoFinal =
    motor === null
      ? undefined
      : medidor.tipo === 'odometro'
        ? `Rodou no turno: ${formatarDecimal(motor, 1)} km.`
        : `Motor no turno: ${formatarDecimal(motor, 1)} h.`;
  const nomeDoOperador = (cadastros.data?.operadores ?? []).find((o) => o.id === v.operadorId)?.nome ?? null;
  const ajudaDoOperador = marcado('operadorId')
    ? operadorProposto?.origem === 'ultimo-turno'
      ? `Operou o último turno de ${codigo}, em ${dataDoUltimo}.`
      : 'Você, porque tem autorização para operar esta máquina.'
    : 'Sem operador, o turno entra sem quem operou.';
  const horario =
    v.inicio !== '' && v.fim !== ''
      ? { valor: `${v.inicio} às ${v.fim}`, vazio: false }
      : horarioCadastrado
        ? { valor: `${horarioCadastrado.inicio} às ${horarioCadastrado.fim}, do turno cadastrado`, vazio: false }
        : { valor: 'Hora deste lançamento', vazio: true };
  const nomeDaCausa = (cadastros.data?.motivos ?? []).find((m) => m.id === v.causaId);

  const campoDaCausa = (
    <Campo
      id={`${base}-causa`}
      rotulo="Causa da parada"
      opcional
      erro={erros.causa}
      ajuda="Explica a espera e a manutenção do turno."
    >
      {cadastros.isPending ? (
        <ControleCarregando oQue="Carregando as causas" />
      ) : (
        <ListaDeEscolha
          id={`${base}-causa`}
          rotulo="Causa da parada"
          valor={v.causaId}
          aoMudar={(valor) => {
            mudar('causaId', valor, 'causa');
          }}
          aoSair={() => {
            conferir('causa');
          }}
          rotuloVazio="Sem causa"
          opcoes={(cadastros.data?.motivos ?? []).map((m) => ({
            valor: m.id,
            rotulo: m.nome,
            etiquetas: [{ texto: m.codigo, tom: 'neutro' as const, titulo: 'Código da causa' }],
            detalhe: m.evitavel ? 'evitável' : 'não evitável',
          }))}
          idDoErro={erros.causa ? `${base}-causa-erro` : undefined}
        />
      )}
    </Campo>
  );

  return (
    <form onSubmit={enviar} noValidate className="cartao formulario-grade min-w-0 p-6">
      {/* A recusa do servidor no TOPO do formulário (p.23), onde a pessoa começa a corrigir. */}
      {recusa === null ? null : (
        <div role="alert" className="campo-linha flex flex-col gap-1 rounded-controle border border-borda bg-rebaixada p-4">
          <span className="txt-corpo text-tinta">{fraseDoErro(recusa.erro)}</span>
          <span className="txt-apoio text-tinta-secundaria">{recusa.comoResolver}</span>
        </div>
      )}

      {recuperado === null ? null : (
        <div
          role="status"
          className="bloco-rebaixado campo-linha flex flex-wrap items-center justify-between gap-2 px-4 py-2"
        >
          <span className="txt-apoio text-tinta-secundaria">
            {`Rascunho de ${formatarDataHora(new Date(recuperado.salvoEm))} recuperado: o turno continua de onde você parou.`}
          </span>
          <Botao nivel="terciario" onClick={descartarRascunho}>
            Descartar rascunho
          </Botao>
        </div>
      )}

      {/* ---- Linha 1: qual máquina e que dia ---- */}
      <div className="campo-largo">
        <Campo
          id={`${base}-equipamento`}
          rotulo="Equipamento"
          erro={erros.equipamento}
          {...(equipamentoInicial !== '' && v.equipamentoId === equipamentoInicial ? { ajuda: 'A máquina do filtro da lista. Troque se for outra.' } : {})}
        >
          {/* As opções ainda vêm (p.32): a lista só com "Escolha a máquina"
              deixava escolher o nada, e escondia a do rascunho recuperado. */}
          {cadastros.isPending ? (
            <ControleCarregando oQue="Carregando as máquinas" />
          ) : (
            /* TODA MÁQUINA (decisão 51, 0113): o caminhão de odômetro aponta
               turno em km. O banco grava o tipo do medidor com o turno, e a
               hora de motor dele fica nula — km não é hora (o defeito da 0086).
               O código interno e a placa em etiqueta (31/10/2026). */
            <ListaDeEscolha
              id={`${base}-equipamento`}
              rotulo="Equipamento"
              valor={v.equipamentoId}
              aoMudar={escolherMaquina}
              aoSair={() => {
                conferir('equipamento');
              }}
              rotuloVazio="Escolha a máquina"
              opcoes={comVisualDaFrota(equipamentos.map((e) => opcaoDaMaquina(e)))}
              idDoErro={erros.equipamento ? `${base}-equipamento-erro` : undefined}
              adesivo
            />
          )}
        </Campo>
      </div>

      <Campo id={`${base}-dia`} rotulo="Dia" {...(v.dia === padrao.dia ? { ajuda: 'Hoje. Troque se o turno foi antes.' } : {})}>
        <input
          id={`${base}-dia`}
          type="date"
          className="controle w-full"
          value={v.dia}
          onChange={(e) => {
            mudar('dia', e.target.value);
          }}
        />
      </Campo>

      {/* ---- Linha 2: o turno e os dois horímetros ---- */}
      <Campo id={`${base}-turno`} rotulo="Turno" erro={erros.turno} preenchido={marcado('turno')} {...(ajudaDoTurno ? { ajuda: ajudaDoTurno } : {})}>
        <input
          id={`${base}-turno`}
          type="text"
          autoComplete="off"
          className="controle w-full"
          placeholder="1º turno"
          list={`${base}-turnos`}
          value={v.turno}
          {...invalido('turno', `${base}-turno`)}
          onChange={(e) => {
            mudar('turno', e.target.value, 'turno');
          }}
          onBlur={() => {
            conferir('turno');
          }}
        />
        <datalist id={`${base}-turnos`}>
          {turnosDoDia.map((nome) => (
            <option key={nome} value={nome} />
          ))}
        </datalist>
      </Campo>

      <Campo
        id={`${base}-inicial`}
        rotulo={`${medidor.nome} no início`}
        erro={erros.inicial}
        preenchido={marcado('inicial')}
        {...(ajudaDoInicial ? { ajuda: ajudaDoInicial } : {})}
      >
        <div className="flex items-center gap-2">
          <input
            id={`${base}-inicial`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="controle w-full min-w-0"
            value={v.inicial}
            {...invalido('inicial', `${base}-inicial`)}
            onChange={(e) => {
              mudar('inicial', e.target.value, 'inicial');
            }}
            onBlur={() => {
              /* O fim depende do início: se ele já foi preenchido, a relação
                 entre os dois é conferida de novo ao sair daqui. */
              if (v.final.trim() === '') conferir('inicial');
              else conferir('inicial', 'final');
            }}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">{medidor.unidade}</span>
        </div>
      </Campo>

      {/* O MOTOR A CADA TECLA, na ajuda do campo do fim: é ali que a pessoa
          confere se trocou um dígito, antes de sair do campo. */}
      <Campo id={`${base}-final`} rotulo={`${medidor.nome} no fim`} erro={erros.final} {...(ajudaDoFinal ? { ajuda: ajudaDoFinal } : {})}>
        <div className="flex items-center gap-2">
          <input
            id={`${base}-final`}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            className="controle w-full min-w-0"
            value={v.final}
            {...invalido('final', `${base}-final`)}
            onChange={(e) => {
              mudar('final', e.target.value, 'final');
            }}
            onBlur={() => {
              conferir('final');
            }}
          />
          <span className="txt-corpo shrink-0 text-tinta-apagada">{medidor.unidade}</span>
        </div>
      </Campo>

      {/* ---- Linha 3: as quatro horas do turno, lado a lado ---- */}
      <fieldset className="campo-linha flex min-w-0 flex-col gap-2">
        <legend className="txt-rotulo-campo mb-2">Horas do turno</legend>
        <div className="grid grid-cols-2 gap-4 @min-[640px]:grid-cols-4">
          {hora('producao', 'Produção')}
          {hora('espera', 'Espera')}
          {hora('deslocamento', 'Deslocamento')}
          {hora('manutencao', 'Manutenção')}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <p className="txt-apoio text-tinta-secundaria">
            Somam <Medida valor={formatarDecimal(somaDasQuatro, 1)} unidade="h" /> do turno. As quatro dividem o turno; o motor anda durante elas e não soma.
          </p>
          {/* O ATALHO DO TURNO TODO EM PRODUÇÃO: o turno comum é motor
              trabalhando. Só aparece com as quatro em branco, e só escreve
              quando a pessoa pede. */}
          {medidor.tipo === 'horimetro' && motor !== null && motor > 0 && motor <= HORAS_MAXIMAS_DO_TURNO && nenhumaHora ? (
            <Botao
              nivel="fantasma"
              tamanho="p"
              onClick={() => {
                mudar('producao', textoDoCampo(Math.round(motor * 100) / 100), 'soma');
              }}
            >
              {`Lançar ${formatarDecimal(motor, 1)} h em produção`}
            </Botao>
          ) : null}
        </div>
        {erros.soma ? (
          <span id={`${base}-soma-erro`} role="alert" className="txt-ajuda text-erro">
            {erros.soma}
          </span>
        ) : null}
      </fieldset>

      {causaAVista ? <div className="campo-largo">{campoDaCausa}</div> : null}

      {/* ---- Quem e quando: o que o sistema sabe, em etiqueta; os campos atrás do botão ---- */}
      <LinhaDoPreenchido
        rotulo="Quem e quando"
        aberta={ajustando}
        aoAlternar={() => {
          setAjustando((aberta) => !aberta);
        }}
        rotuloDoBotao="operador e horário"
        idDoConteudo={`${base}-quem-e-quando`}
      >
        <EtiquetaDoPreenchido rotulo="Operador" valor={nomeDoOperador ?? 'Não informado'} vazia={nomeDoOperador === null} marcada={marcado('operadorId')} />
        <EtiquetaDoPreenchido rotulo="Horário" valor={horario.valor} vazia={horario.vazio} />
        <EtiquetaDoPreenchido rotulo="Atividade" valor={v.atividade.trim() === '' ? 'Sem atividade' : v.atividade} vazia={v.atividade.trim() === ''} />
        {causaAVista ? null : <EtiquetaDoPreenchido rotulo="Causa" valor={nomeDaCausa ? `${nomeDaCausa.codigo} · ${nomeDaCausa.nome}` : 'Sem causa'} vazia={!nomeDaCausa} />}
      </LinhaDoPreenchido>

      {ajustando ? (
        /* A GAVETA ABERTA é a mesma grade do formulário, em bloco rebaixado:
           os campos entram nas colunas da tela, sem segunda grade a medir. */
        <div id={`${base}-quem-e-quando`} className="campo-linha formulario-grade bloco-rebaixado p-4">
          <Campo id={`${base}-operador`} rotulo="Operador" opcional preenchido={marcado('operadorId')} ajuda={ajudaDoOperador}>
            {cadastros.isPending ? (
              <ControleCarregando oQue="Carregando os operadores" />
            ) : (
              <ListaDeEscolha
                id={`${base}-operador`}
                rotulo="Operador"
                valor={v.operadorId}
                aoMudar={(valor) => {
                  mudar('operadorId', valor);
                }}
                rotuloVazio="Não informado"
                opcoes={(cadastros.data?.operadores ?? []).map((o) => opcaoDaPessoa(o))}
              />
            )}
          </Campo>

          {/* A HORA DO TURNO (0121): é nela que o horímetro final é registrado. */}
          <Campo
            id={`${base}-hora`}
            rotulo="Começou às"
            opcional
            erro={erros.hora}
            {...(horarioCadastrado && v.inicio === '' && v.fim === '' ? { ajuda: `Em branco, vale o turno cadastrado: ${horarioCadastrado.inicio}.` } : {})}
          >
            <input
              id={`${base}-hora`}
              type="time"
              className="controle w-full"
              value={v.inicio}
              {...invalido('hora', `${base}-hora`)}
              onChange={(e) => {
                mudar('inicio', e.target.value, 'hora');
              }}
              onBlur={() => {
                conferir('hora');
              }}
            />
          </Campo>

          <Campo
            id={`${base}-fim`}
            rotulo="Terminou às"
            opcional
            ajuda="A hora em que o horímetro final foi lido. Sem ela, vale o fim do turno cadastrado com este nome ou a hora deste lançamento."
          >
            <input
              id={`${base}-fim`}
              type="time"
              className="controle w-full"
              value={v.fim}
              onChange={(e) => {
                mudar('fim', e.target.value, 'hora');
              }}
              onBlur={() => {
                conferir('hora');
              }}
            />
          </Campo>

          <div className="campo-largo">
            <Campo
              id={`${base}-atividade`}
              rotulo="Atividade"
              opcional
              {...(ultimo?.atividade ? { ajuda: `No último turno de ${codigo}: ${ultimo.atividade}.` } : {})}
            >
              <input
                id={`${base}-atividade`}
                type="text"
                autoComplete="off"
                className="controle w-full"
                placeholder="Corte no talude norte"
                value={v.atividade}
                onChange={(e) => {
                  mudar('atividade', e.target.value);
                }}
              />
            </Campo>
          </div>

          {causaAVista ? null : campoDaCausa}
        </div>
      ) : null}

      {/* Barra de ações fixa (R01): Cancelar à esquerda, primário à direita. */}
      <div className="barra-de-acoes campo-linha flex-wrap">
        <Botao nivel="secundario" type="button" onClick={cancelar}>
          Cancelar
        </Botao>
        <Botao nivel="primario" type="submit" disabled={apontar.isPending}>
          {apontar.isPending ? 'Gravando…' : 'Gravar apontamento'}
        </Botao>
      </div>
    </form>
  );
}
